import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { LANGUAGES, CEFR_LEVELS } from '@/lib/languages'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { ChapterRoadmap, type RoadmapUnit, type UnitStatus } from '@/components/ui/chapter-roadmap'
import { ChapterCompleteCard } from '@/components/ui/chapter-complete-card'
import { Clock, Settings as SettingsIcon, LogOut, Mic, MessageCircle } from 'lucide-react'
import type { UserRoadmap, UserUnitProgress, OnboardingStatus, LessonKey } from '@/types/database'

// Friendly tutor avatar SVG component
function TutorAvatar() {
  return (
    <div className="relative">
      {/* Glow effect behind avatar */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 to-orange-400/30 rounded-full blur-xl scale-150 animate-pulse-soft" />

      {/* Avatar container */}
      <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
        {/* Simple friendly face */}
        <svg viewBox="0 0 100 100" className="w-20 h-20">
          {/* Face background */}
          <circle cx="50" cy="50" r="45" fill="#FEF3C7" />

          {/* Rosy cheeks */}
          <circle cx="25" cy="55" r="8" fill="#FECACA" opacity="0.6" />
          <circle cx="75" cy="55" r="8" fill="#FECACA" opacity="0.6" />

          {/* Eyes */}
          <ellipse cx="35" cy="42" rx="6" ry="7" fill="#1F2937" />
          <ellipse cx="65" cy="42" rx="6" ry="7" fill="#1F2937" />

          {/* Eye highlights */}
          <circle cx="37" cy="40" r="2" fill="white" />
          <circle cx="67" cy="40" r="2" fill="white" />

          {/* Warm smile */}
          <path
            d="M 35 62 Q 50 78 65 62"
            fill="none"
            stroke="#1F2937"
            strokeWidth="3"
            strokeLinecap="round"
          />

          {/* Subtle eyebrows */}
          <path d="M 28 32 Q 35 28 42 32" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
          <path d="M 58 32 Q 65 28 72 32" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* Online indicator */}
      <div className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
        <Mic className="w-2.5 h-2.5 text-white" />
      </div>
    </div>
  )
}

// Speech bubble component
function SpeechBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {/* Bubble */}
      <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-md border border-amber-100">
        {children}
      </div>
      {/* Tail */}
      <div className="absolute -top-0 -left-2 w-4 h-4 bg-white border-l border-t border-amber-100 transform rotate-[-45deg] rounded-tl-sm" />
    </div>
  )
}

// Introduction prompt component for new users who haven't completed onboarding
function IntroductionPrompt({ targetLanguage, onStart }: { targetLanguage: string; onStart: () => void }) {
  return (
    <div className="relative">
      {/* Decorative background elements */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-rose-200/30 rounded-full blur-3xl" />

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-3xl border border-amber-200/50 shadow-xl overflow-hidden">
        {/* Top decorative bar */}
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

        <div className="p-8 md:p-10">
          {/* Avatar and greeting section */}
          <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
            {/* Avatar with animation */}
            <div className="animate-bounce-in flex-shrink-0 mx-auto md:mx-0">
              <TutorAvatar />
            </div>

            {/* Speech bubble greeting */}
            <div className="flex-1 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <SpeechBubble>
                <p className="text-lg md:text-xl text-gray-800 font-medium leading-relaxed">
                  Hi there! I'm <span className="text-amber-600 font-semibold">Kai</span>, your language tutor.
                </p>
                <p className="text-gray-600 mt-2">
                  I'm excited to help you learn <span className="font-medium text-gray-800">{targetLanguage}</span>!
                </p>
              </SpeechBubble>
            </div>
          </div>

          {/* What to expect section */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-amber-100/50 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-4 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              What we'll do
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-700 text-sm font-medium">1</span>
                </div>
                <p className="text-gray-700">Chat about your goals and why you want to learn</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-700 text-sm font-medium">2</span>
                </div>
                <p className="text-gray-700">Share your interests so lessons feel relevant to you</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-700 text-sm font-medium">3</span>
                </div>
                <p className="text-gray-700">Try speaking a bit so I can understand your level</p>
              </div>
            </div>
          </div>

          {/* CTA section */}
          <div className="text-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Button
              size="lg"
              onClick={onStart}
              className="w-full sm:w-auto px-10 h-14 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] rounded-xl"
            >
              <Mic className="w-5 h-5 mr-2" />
              Start Our Chat
            </Button>

            {/* Time estimate badge */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100/60 text-amber-800 text-sm">
              <Clock className="w-4 h-4" />
              <span>About 5 minutes</span>
              <span className="text-amber-600">•</span>
              <span>Voice conversation</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Generating roadmap loading state component
function GeneratingRoadmapState() {
  return (
    <div className="relative">
      {/* Decorative background elements */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-rose-200/30 rounded-full blur-3xl" />

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-3xl border border-amber-200/50 shadow-xl overflow-hidden">
        {/* Top decorative bar with animation */}
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse" />
        </div>

        <div className="p-8 md:p-10">
          {/* Avatar and message section */}
          <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
            {/* Avatar with working animation */}
            <div className="animate-bounce-in flex-shrink-0 mx-auto md:mx-0">
              <TutorAvatar />
            </div>

            {/* Speech bubble */}
            <div className="flex-1 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <SpeechBubble>
                <p className="text-lg md:text-xl text-gray-800 font-medium leading-relaxed">
                  Great conversation! I'm building your personalized learning plan...
                </p>
                <p className="text-gray-600 mt-2">
                  This will only take a moment.
                </p>
              </SpeechBubble>
            </div>
          </div>

          {/* Loading progress section */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-amber-100/50 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-4">
              {/* Animated loader */}
              <div className="relative w-12 h-12 flex-shrink-0">
                <div className="absolute inset-0 rounded-full border-4 border-amber-200" />
                <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
              </div>

              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-1">
                  Creating Your Roadmap
                </h3>
                <p className="text-gray-600 text-sm">
                  Analyzing your goals, interests, and level to create the perfect curriculum...
                </p>
              </div>
            </div>

            {/* Progress steps */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">Introduction completed</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">Level assessed</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
                <span className="text-gray-700">Generating personalized units...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// State for roadmap and all units
interface RoadmapState {
  units: RoadmapUnit[]
  roadmap: UserRoadmap | null
  loading: boolean
  chapterComplete: boolean
  completedUnits: Array<{ id: string; task: string }>
}

// Helper to determine unit status
function getUnitStatus(
  unitIndex: number,
  currentUnitIndex: number,
  isSubscribed: boolean
): UnitStatus {
  if (unitIndex < currentUnitIndex) return 'completed'
  if (unitIndex === currentUnitIndex) return 'current'
  // Unit 1 (index 0) is always free, Units 2-6 (index 1+) require subscription
  if (unitIndex > 0 && !isSubscribed) return 'premium-locked'
  return 'locked'
}

export function Home() {
  const navigate = useNavigate()
  const { profile, signOut } = useAuth()
  const [roadmapState, setRoadmapState] = useState<RoadmapState>({
    units: [],
    roadmap: null,
    loading: true,
    chapterComplete: false,
    completedUnits: [],
  })
  // Fresh onboarding status fetched directly from DB (avoids stale AuthContext)
  const [freshOnboardingStatus, setFreshOnboardingStatus] = useState<OnboardingStatus | null>(null)

  const targetLanguage = LANGUAGES.find(l => l.code === profile?.target_language)
  const currentLevel = CEFR_LEVELS.find(l => l.code === profile?.level)

  // Use fresh status if available, fallback to cached profile
  const onboardingStatus: OnboardingStatus = freshOnboardingStatus ??
    (profile?.onboarding_status as OnboardingStatus) ?? 'pending'
  const needsIntroduction = onboardingStatus === 'pending'
  const isGeneratingRoadmap = ['intro_completed', 'generating'].includes(onboardingStatus)


  // Check subscription status (placeholder - will be populated from profile)
  const isSubscribed: boolean = profile?.subscription_status === 'active' ||
    (profile?.subscription_status === 'trial' &&
      profile?.subscription_expires_at != null &&
      new Date(profile.subscription_expires_at) > new Date()) || false

  // Use cached profile status directly (avoids hanging fetch issues)
  useEffect(() => {
    if (profile?.id) {
      setFreshOnboardingStatus(profile.onboarding_status as OnboardingStatus ?? 'pending')
    }
  }, [profile?.id, profile?.onboarding_status])

  // Poll for onboarding status changes while generating roadmap
  useEffect(() => {
    if (!isGeneratingRoadmap || !profile?.id) return

    const pollInterval = setInterval(async () => {
      const { data } = await supabase
        .from('users')
        .select('onboarding_status')
        .eq('id', profile.id)
        .single()

      const status = (data as { onboarding_status: OnboardingStatus } | null)?.onboarding_status
      if (status === 'ready') {
        // Update local state to trigger re-render with roadmap view
        setFreshOnboardingStatus('ready')
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [isGeneratingRoadmap, profile?.id])

  // Fetch all units and progress for onboarded users
  useEffect(() => {
    async function fetchRoadmapUnits() {
      if (!profile?.id || needsIntroduction || isGeneratingRoadmap) {
        setRoadmapState(prev => ({ ...prev, loading: false }))
        return
      }

      try {
        // Get user's active roadmap
        const { data: roadmapData } = await supabase
          .from('user_roadmap')
          .select('*')
          .eq('user_id', profile.id)
          .eq('status', 'active')
          .maybeSingle()

        const roadmap = roadmapData as UserRoadmap | null

        if (!roadmap || !roadmap.unit_ids || roadmap.unit_ids.length === 0) {
          setRoadmapState({ units: [], roadmap: null, loading: false, chapterComplete: false, completedUnits: [] })
          return
        }

        // Check if chapter is complete (current_unit_index >= number of units)
        const isChapterComplete = roadmap.current_unit_index >= roadmap.unit_ids.length

        // Fetch ALL units in this roadmap (including lesson_config)
        const { data: allUnitsDataRaw } = await supabase
          .from('units')
          .select('id, task, description, difficulty_order, lesson_config')
          .in('id', roadmap.unit_ids)

        const allUnitsData = allUnitsDataRaw as Array<{
          id: string
          task: string
          description: string | null
          difficulty_order: number
          lesson_config: LessonKey[] | null
        }> | null

        // Fetch ALL progress records for these units
        const { data: allProgressDataRaw } = await supabase
          .from('user_unit_progress')
          .select('*')
          .eq('user_id', profile.id)
          .in('unit_id', roadmap.unit_ids)

        const allProgressData = allProgressDataRaw as UserUnitProgress[] | null

        // Create a map of unit_id -> progress
        const progressMap = new Map<string, UserUnitProgress>()
        allProgressData?.forEach(p => {
          progressMap.set(p.unit_id, p)
        })

        // Sort units to match the order in unit_ids and build RoadmapUnit array
        const roadmapUnits: RoadmapUnit[] = roadmap.unit_ids
          .map((unitId, index) => {
            const unit = allUnitsData?.find(u => u.id === unitId)
            if (!unit) return null

            const progress = progressMap.get(unitId)
            const status = getUnitStatus(index, roadmap.current_unit_index, isSubscribed)

            return {
              id: unit.id,
              task: unit.task,
              description: unit.description,
              order: index,
              status,
              lessons: progress?.lessons_completed,
              lessonConfig: unit.lesson_config ?? undefined,
            }
          })
          .filter(Boolean) as RoadmapUnit[]

        if (isChapterComplete) {
          // For completion card
          const completedUnits = roadmapUnits.map(u => ({ id: u.id, task: u.task }))

          setRoadmapState({
            units: roadmapUnits,
            roadmap: roadmap as UserRoadmap,
            loading: false,
            chapterComplete: true,
            completedUnits,
          })
          return
        }

        setRoadmapState({
          units: roadmapUnits,
          roadmap: roadmap as UserRoadmap,
          loading: false,
          chapterComplete: false,
          completedUnits: [],
        })
      } catch (error) {
        console.error('Error fetching roadmap units:', error)
        setRoadmapState({ units: [], roadmap: null, loading: false, chapterComplete: false, completedUnits: [] })
      }
    }

    fetchRoadmapUnits()
  }, [profile?.id, needsIntroduction, isGeneratingRoadmap, isSubscribed])

  // Handler for starting a lesson from the roadmap
  const handleStartLesson = (unitId: string, lessonType: string) => {
    navigate('/practice', {
      state: {
        lessonType,
        unitId,
      },
    })
  }

  // Handler for free conversation
  const handleFreeConversation = () => {
    navigate('/practice', {
      state: {
        lessonType: 'conversation',
      },
    })
  }

  // Handler for starting a checkpoint session
  const handleStartCheckpoint = () => {
    navigate('/practice', {
      state: {
        lessonType: 'checkpoint',
      },
    })
  }

  // Handler for subscription (placeholder)
  const handleSubscribe = () => {
    // TODO: Implement RevenueCat integration
    console.log('Subscribe clicked')
  }

  // Check if we have a roadmap to show
  const hasRoadmap = !needsIntroduction && roadmapState.units.length > 0 && roadmapState.roadmap

  // Check if chapter is complete
  const isChapterComplete = !needsIntroduction && roadmapState.chapterComplete && roadmapState.roadmap

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-r from-amber-50/60 via-white to-orange-50/60 border-b border-amber-200/30 shadow-sm">
        <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <h1 className="text-lg md:text-xl font-semibold text-gray-800">AI Language Tutor</h1>
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            {!needsIntroduction && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/history')}>
                <Clock className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">History</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
              <SettingsIcon className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Settings</span>
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              <LogOut className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {freshOnboardingStatus === null ? (
            // Loading state while fetching fresh onboarding status
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-4 border-amber-200 border-t-amber-500 animate-spin" />
            </div>
          ) : needsIntroduction ? (
            <IntroductionPrompt
              targetLanguage={targetLanguage?.name || 'your new language'}
              onStart={() => navigate('/practice')}
            />
          ) : isGeneratingRoadmap ? (
            <GeneratingRoadmapState />
          ) : roadmapState.loading ? (
            // Loading state while roadmap is being fetched
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-4 border-amber-200 border-t-amber-500 animate-spin" />
            </div>
          ) : isChapterComplete ? (
            <ChapterCompleteCard
              chapterNumber={roadmapState.roadmap?.chapter_number || 1}
              completedUnits={roadmapState.completedUnits}
              onStartCheckpoint={handleStartCheckpoint}
            />
          ) : hasRoadmap ? (
            <ChapterRoadmap
              chapterNumber={roadmapState.roadmap!.chapter_number || 1}
              level={currentLevel?.code || 'A1'}
              units={roadmapState.units}
              isSubscribed={isSubscribed}
              lessonConfig={roadmapState.roadmap!.lesson_config as LessonKey[] | undefined}
              onStartLesson={handleStartLesson}
              onSubscribe={handleSubscribe}
              onFreeConversation={handleFreeConversation}
            />
          ) : (
            // Fallback for edge case where user is ready but has no roadmap
            <div className="text-center py-12">
              <p className="text-stone-600 mb-4">No learning roadmap found.</p>
              <Button onClick={() => navigate('/practice')}>
                Start Introduction
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
